import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';


/**
 * RateLimitContext
 * Tracks unlock timestamps for rate-limited operation keys and exposes countdowns.
 * Currently listens for 'rate-limit' CustomEvents dispatched by the axios 429 interceptor.
 */
const RateLimitContext = createContext(null);

export const RateLimitProvider = ({ children }) => {
  const [locks, setLocks] = useState(() => {
    // Hydrate initial known lock (generic sensitive op) from localStorage
    const existing = localStorage.getItem('rateLimit:sensitiveOp:unlockAt');
    if (existing) {
      const num = parseInt(existing, 10);
      if (!isNaN(num) && num > Date.now()) {
        return { sensitiveOp: num };
      }
    }
    return {};
  });

  const timersRef = useRef({});

  const clearTimer = (key) => {
    if (timersRef.current[key]) {
      clearTimeout(timersRef.current[key]);
      delete timersRef.current[key];
    }
  };

  const scheduleUnlockNotify = useCallback((key, unlockAt) => {
    clearTimer(key);
    const ms = unlockAt - Date.now();
    if (ms <= 0) return;
    timersRef.current[key] = setTimeout(() => {
      setLocks(prev => {
        const updated = { ...prev };
        // If still in future don't remove
        if (updated[key] && updated[key] <= Date.now()) {
          delete updated[key];
        }
        return updated;
      });
      window.dispatchEvent(new CustomEvent('rate-limit-unlocked', { detail: { key } }));
    }, ms + 25); // small buffer
  }, []);

  // Listen for new limit events
  useEffect(() => {
    const handler = (e) => {
      const { key, unlockAt } = e.detail || {};
      if (!key || !unlockAt) return;
      setLocks(prev => ({ ...prev, [key]: unlockAt }));
      scheduleUnlockNotify(key, unlockAt);
    };
    window.addEventListener('rate-limit', handler);
    return () => window.removeEventListener('rate-limit', handler);
  }, [scheduleUnlockNotify]);

  // Optional: broadcast unlock via notification system if available
  useEffect(() => {
    const unlockHandler = (e) => {
      const { key } = e.detail || {};
      if (!key) return;
      // Attempt to use global notification context if it's been attached earlier in the tree
      try {
        // We avoid direct import here to prevent circular dependency; consumers can listen to this event if needed.
        // If we later want to integrate directly, we can refactor to accept a prop callback.
      } catch (_) { /* ignore */ }
    };
    window.addEventListener('rate-limit-unlocked', unlockHandler);
    return () => window.removeEventListener('rate-limit-unlocked', unlockHandler);
  }, []);

  // Reschedule timers if locks change (e.g. hydration)
  useEffect(() => {
    Object.entries(locks).forEach(([key, unlockAt]) => scheduleUnlockNotify(key, unlockAt));
  }, [locks, scheduleUnlockNotify]);

  const getRemainingMs = useCallback((key) => {
    const unlockAt = locks[key];
    if (!unlockAt) return 0;
    return Math.max(0, unlockAt - Date.now());
  }, [locks]);

  const isLocked = useCallback((key) => getRemainingMs(key) > 0, [getRemainingMs]);

  const value = {
    locks,
    isLocked,
    getRemainingMs
  };

  return (
    <RateLimitContext.Provider value={value}>
      {children}
    </RateLimitContext.Provider>
  );
};

export const useRateLimit = () => {
  const ctx = useContext(RateLimitContext);
  if (!ctx) throw new Error('useRateLimit must be used within RateLimitProvider');
  return ctx;
};
